/**
 * Placement Validation Module
 *
 * Provides validation logic for piece placement on the game grid:
 * - Boundary checking (10x10 grid constraints)
 * - Overlap detection between pieces
 * - Complete placement validation
 *
 * All functions use real coordinate calculations, no mocking.
 */

const GRID_SIZE = 10; // 10x10 grid (0-9 for both x and y)
const TOTAL_PIECES = 5; // Game requires exactly 5 pieces

/**
 * Check if a piece shape at given position stays within grid boundaries
 * @param {Array<{x: number, y: number}>} shape - Piece shape coordinates
 * @param {{x: number, y: number}} position - Position to check
 * @returns {boolean} True if piece is fully within bounds
 */
export function isWithinBounds(shape, position) {
  if (!shape || shape.length === 0) {
    return false;
  }

  // Check if position itself is negative
  if (position.x < 0 || position.y < 0) {
    return false;
  }

  // Check each square of the piece
  for (const coord of shape) {
    const absoluteX = position.x + coord.x;
    const absoluteY = position.y + coord.y;

    // Check boundaries (0-9 for both x and y)
    if (
      absoluteX < 0 ||
      absoluteX >= GRID_SIZE ||
      absoluteY < 0 ||
      absoluteY >= GRID_SIZE
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Check if two pieces overlap
 * @param {Array<{x: number, y: number}>} shape1 - First piece shape
 * @param {{x: number, y: number}} position1 - First piece position
 * @param {Array<{x: number, y: number}>} shape2 - Second piece shape
 * @param {{x: number, y: number}} position2 - Second piece position
 * @returns {boolean} True if pieces overlap
 */
export function hasOverlap(shape1, position1, shape2, position2) {
  // Calculate absolute positions for first piece
  const cells1 = shape1.map((coord) => ({
    x: position1.x + coord.x,
    y: position1.y + coord.y,
  }));

  // Calculate absolute positions for second piece
  const cells2 = shape2.map((coord) => ({
    x: position2.x + coord.x,
    y: position2.y + coord.y,
  }));

  // Check if any cells overlap
  for (const cell1 of cells1) {
    for (const cell2 of cells2) {
      if (cell1.x === cell2.x && cell1.y === cell2.y) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Validate if a piece can be placed at the given position
 * @param {Object} newPiece - Piece to place (with shape, position, rotation, type)
 * @param {Array<Object>} existingPieces - Already placed pieces
 * @returns {{valid: boolean, error: string|null}} Validation result
 */
export function validatePlacement(newPiece, existingPieces = []) {
  const { shape, position } = newPiece;

  // Check if piece is within bounds
  if (!isWithinBounds(shape, position)) {
    return {
      valid: false,
      error: 'Piece is out of bounds',
    };
  }

  // Check for overlaps with existing pieces
  for (const existingPiece of existingPieces) {
    if (
      hasOverlap(shape, position, existingPiece.shape, existingPiece.position)
    ) {
      return {
        valid: false,
        error: 'Piece overlaps with existing piece',
      };
    }
  }

  return {
    valid: true,
    error: null,
  };
}

/**
 * Quick check if piece can be placed (simplified version of validatePlacement)
 * @param {Array<{x: number, y: number}>} shape - Piece shape
 * @param {{x: number, y: number}} position - Position to check
 * @param {Array<Object>} existingPieces - Already placed pieces
 * @returns {boolean} True if piece can be placed
 */
export function canPlacePiece(shape, position, existingPieces = []) {
  // Check bounds
  if (!isWithinBounds(shape, position)) {
    return false;
  }

  // Check overlaps
  for (const existingPiece of existingPieces) {
    if (
      hasOverlap(shape, position, existingPiece.shape, existingPiece.position)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Get all cells occupied by placed pieces
 * @param {Array<Object>} pieces - Array of placed pieces
 * @returns {Array<{x: number, y: number}>} Array of occupied cell coordinates
 */
export function getAllOccupiedCells(pieces = []) {
  const occupied = [];

  for (const piece of pieces) {
    const { shape, position } = piece;

    for (const coord of shape) {
      occupied.push({
        x: position.x + coord.x,
        y: position.y + coord.y,
      });
    }
  }

  return occupied;
}

/**
 * Check if all required pieces are placed (exactly 5)
 * @param {Array<Object>} pieces - Array of placed pieces
 * @returns {boolean} True if exactly 5 pieces are placed
 */
export function areAllPiecesPlaced(pieces = []) {
  return pieces.length === TOTAL_PIECES;
}
